import { useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Link, useHydrated } from '@tanstack/react-router';
import { Plural, useLingui } from '@lingui/react/macro';
import { FileSignatureIcon } from 'lucide-react';
import { Button } from '@worldcoo/ui/components/button';
import { useNgoProfileQuery } from '@/lib/data-hooks';
import { trpc } from '@/lib/trpc';
import { formatRejectionDate } from './contracts/rejection-date';
import { NgoNoticeBar, NgoNoticeDialog, NgoNoticeDialogAction } from './ngo-notice';
import { SectionBoundary } from './section-states';

const SEEN_KEY = 'ngo-contract-notice-seen';

/** FR-37's rungs as the area shows them; core decides which one the clock reached. */
type LadderStage = 'available' | 'warning' | 'last-month' | 'blocked';

type ContractNotice = (
	| { kind: 'awaiting-signature' }
	| { kind: 'copy-rejected'; rejection: { reason: string; at: number } }
) & { ladder?: { stage: LadderStage; daysLeft: number; blocksAt?: number } };

/** Called beside `forgetLegalRepresentativeNotice`, wherever the session or the NGO changes. */
export function forgetContractSignatureNotice(): void {
	try {
		sessionStorage.removeItem(SEEN_KEY);
	} catch {
		// Nothing to forget when the browser refuses storage: the dialog was never hidden.
	}
}

/**
 * FR-19: while a contract waits for the NGO's signature, a bar on every page and a dialog once per session, in
 * Comms' words (VSNI-96). FR-37: from day 10 the bar warns and names the date new projects stop, "Aviso 1" counts
 * the days from day 30, and from day 60 both say the deadline passed. FR-90: a rejected copy says so instead.
 */
export function ContractSignatureBanner() {
	// A notice that fails to load shows nothing until the next full load, rather than taking the page down with it.
	return (
		<SectionBoundary fallback={null} errorRender={() => null}>
			<ContractSignatureBannerContent />
		</SectionBoundary>
	);
}

function ContractSignatureBannerContent() {
	const ngo = useNgoProfileQuery();
	// Fetched with the page, so the server's HTML has the bar; it is never saved in the browser, so no older copy
	// stands in for it (`isContractNoticeQueryKey`).
	const { data: notice } = useSuspenseQuery(trpc.contract.notice.queryOptions());
	// The dialog reads what this visit already saw, which only the browser knows.
	const hydrated = useHydrated();
	// The representative is the earlier step, and the layout makes room for one bar.
	if (!notice || ngo.awaitsLegalRepresentative) return null;
	const seenAs = `${notice.kind}:${notice.ladder?.stage ?? 'available'}`;

	return (
		<>
			<ContractSignatureBar notice={notice} ngoName={ngo.name ?? ''} />
			{hydrated && <ContractSignatureDialog key={seenAs} notice={notice} seenAs={seenAs} />}
		</>
	);
}

/** In Madrid's time, like the contract's other dates, and in words: the bar puts it in a sentence. */
function formatDeadline(at: number, locale: string): string {
	return new Date(at).toLocaleDateString(locale, { dateStyle: 'long', timeZone: 'Europe/Madrid' });
}

function ContractSignatureBar(props: { notice: ContractNotice; ngoName: string }) {
	const { t, i18n } = useLingui();
	const ngoName = props.ngoName;
	const ladder = props.notice.ladder;
	const deadline = ladder?.blocksAt === undefined ? undefined : formatDeadline(ladder.blocksAt, i18n.locale);

	let text = t`Hello ${ngoName}, the new collaboration agreement is now available for you to sign.`;
	if (props.notice.kind === 'copy-rejected') {
		text = t`Hello ${ngoName}, we could not accept the signed copy you sent. See why and send a new one.`;
	} else if (ladder?.stage === 'blocked') {
		text = t`Hello ${ngoName}, the deadline to sign the new collaboration agreement has passed. Sign it to add new projects again.`;
	} else if (ladder?.stage !== 'available' && deadline) {
		text = t`Hello ${ngoName}, please sign the new collaboration agreement before ${deadline}. From that date you can't add new projects.`;
	}

	return (
		<NgoNoticeBar
			warns={ladder !== undefined && ladder.stage !== 'available'}
			icon={<FileSignatureIcon className="size-4 text-[#35433F]" strokeWidth={1.5} />}
			actions={
				<Button asChild size="sm" variant="secondary" className="shrink-0">
					<Link to="/ngos/settings" search={{ tab: 'contracts' }}>
						{t`Review contract`}
					</Link>
				</Button>
			}
		>
			<span className="text-sm leading-[18px] font-medium">{text}</span>
		</NgoNoticeBar>
	);
}

function ContractSignatureDialog(props: { notice: ContractNotice; seenAs: string }) {
	const { t, i18n } = useLingui();
	// Seen per notice and rung, and remounted on a new one: a copy rejected or a rung reached during the visit opens it
	// again.
	const [open, setOpen] = useState(() => {
		try {
			return sessionStorage.getItem(SEEN_KEY) !== props.seenAs;
		} catch {
			return false;
		}
	});

	const dismiss = () => {
		setOpen(false);
		try {
			sessionStorage.setItem(SEEN_KEY, props.seenAs);
		} catch {
			// A browser refusing storage shows the dialog again next entry, which is the safe direction.
		}
	};

	const stage = props.notice.ladder?.stage;
	const daysLeft = props.notice.ladder?.daysLeft ?? 0;
	let title = t`Important notice`;
	let description = (
		<>{t`The new collaboration agreement is now available. Go to the contracts section to review and sign it.`}</>
	);
	if (props.notice.kind === 'copy-rejected') {
		description = <RejectionText rejection={props.notice.rejection} locale={i18n.locale} />;
	} else if (stage === 'blocked') {
		title = t`Agreement pending signature`;
		description = (
			<>{t`The deadline to sign the new collaboration agreement has passed. To continue submitting new projects and be eligible for funding from our partner companies, you need to sign the new agreement.`}</>
		);
	} else if (stage === 'last-month') {
		title = t`Important notice: contract pending signature`;
		description = (
			<>
				{t`It has been 30 days since we informed you about the renewal of your collaboration agreement, and it is still awaiting signature.`}{' '}
				<Plural
					value={daysLeft}
					one="Please go to the contracts section to review and sign the new agreement within the next # day. If you do not sign it within this period, the submission of new projects will be blocked."
					other="Please go to the contracts section to review and sign the new agreement within the next # days. If you do not sign it within this period, the submission of new projects will be blocked."
				/>
			</>
		);
	}

	return (
		<NgoNoticeDialog
			open={open}
			onDismiss={dismiss}
			title={title}
			description={description}
			dismissLabel={t`Remind me later`}
			action={
				<NgoNoticeDialogAction onClick={dismiss}>
					<Link to="/ngos/settings" search={{ tab: 'contracts' }}>
						{t`Review contract now`}
					</Link>
				</NgoNoticeDialogAction>
			}
		/>
	);
}

/** The same words as the alert on the Agreements tab, so the NGO reads one message in both places. */
function RejectionText(props: { rejection: { reason: string; at: number }; locale: string }) {
	const { t } = useLingui();
	const date = formatRejectionDate(props.rejection.at, props.locale);
	const reason = props.rejection.reason;

	return (
		<>
			{t`On ${date} we could not accept the signed contract you sent. The reason: ${reason}`}{' '}
			{t`Send a new copy or sign online. Your current terms apply until the contract is signed.`}
		</>
	);
}
